import React, { useState, useEffect } from "react";
import {
  Box,
  CircularProgress,
  Toolbar,
  Typography,
  IconButton,
} from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import config from "./map/config";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  withScriptjs,
  withGoogleMap,
} from "react-google-maps";

import locationImage from "../../assets/Location.png";

const GOOGLE_MAPS_API_KEY = `${config.mapsKey}`;

const macNameMap = {
  "AC:15:18:F7:6C:68": "T&D Tracker",
  "AC:15:18:F8:BA:6C": "TES Tracker",
};

const infoWindowStyle = {
  padding: "8px 12px",
  borderRadius: 8,
  boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
  backgroundColor: "white",
  fontSize: 13,
  maxWidth: 220,
};

const getCenter = (markers) => {
  if (markers.length === 0) return { lat: 30.7046, lng: 76.7179 };
  const latSum = markers.reduce((acc, m) => acc + m.lat, 0);
  const lngSum = markers.reduce((acc, m) => acc + m.lng, 0);
  return { lat: latSum / markers.length, lng: lngSum / markers.length };
};

const customToolbarStyle = {
  backgroundColor: "#e3f2fd",
  color: "#333",
  padding: "16px",
  borderRadius: "8px",
  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  mb: 2,
};

const TrackerMap = withScriptjs(
  withGoogleMap(({ center, markers, hoveredMarkerId, setHoveredMarkerId }) => {
    const mapRef = React.useRef(null);

    useEffect(() => {
      if (mapRef.current && markers.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
        markers.forEach((marker) =>
          bounds.extend(new window.google.maps.LatLng(marker.lat, marker.lng))
        );
        mapRef.current.fitBounds(bounds);
      }
    }, [markers]);

    return (
      <GoogleMap
        ref={mapRef}
        defaultCenter={center}
        defaultZoom={10}
        options={{
          scrollwheel: true,
          gestureHandling: "greedy",
        }}
      >
        {markers.map((marker, idx) => (
          <Marker
            key={idx}
            position={{ lat: marker.lat, lng: marker.lng }}
            title={macNameMap[marker.macAddress] || marker.macAddress}
            icon={{
              url: locationImage,
              scaledSize: new window.google.maps.Size(40, 40),
              anchor: new window.google.maps.Point(20, 20),
            }}
            onMouseOver={() => setHoveredMarkerId(idx)}
            onMouseOut={() => setHoveredMarkerId(null)}
          >
            {hoveredMarkerId === idx && (
              <InfoWindow onCloseClick={() => setHoveredMarkerId(null)}>
                <div style={infoWindowStyle}>
                  <strong>
                    {macNameMap[marker.macAddress] || marker.macAddress}
                  </strong>
                  <br />
                  Battery: {marker.battery}%
                  <br />
                  Latitude: {marker.lat.toFixed(5)}
                  <br />
                  Longitude: {marker.lng.toFixed(5)}
                  <br />
                  Altitude: {marker.altitude?.toFixed(2) ?? "N/A"} m
                  <br />
                  Power: {marker.power === 0 ? "OFF" : "ON"}
                </div>
              </InfoWindow>
            )}
          </Marker>
        ))}
      </GoogleMap>
    );
  })
);

const AllTrackers = () => {
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredMarkerId, setHoveredMarkerId] = useState(null);

  const fetchTrackers = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        "https://argriapi.techexposys.com/machine-tracking/current-coordinates/all?topic=tes/tracker"
      );
      const data = await response.json();

      if (data.success && Array.isArray(data.data)) {
        setMarkers(data.data);
      } else {
        setMarkers([]);
      }
    } catch (error) {
      console.error("Error fetching tracker data:", error);
      setMarkers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrackers();
  }, []);

  const center = getCenter(markers);

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Toolbar sx={customToolbarStyle}>
        <IconButton
          size="large"
          edge="start"
          color="primary"
          aria-label="live tracking"
          sx={{ mr: 2 }}
        >
          <PlayCircleOutlineIcon />
        </IconButton>
        <Typography variant="h6" component="div">
          All GPS Trackers
        </Typography>
      </Toolbar>

      {loading ? (
        <Box
          sx={{
            p: 3,
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ flexGrow: 1 }}>
          <TrackerMap
            center={center}
            markers={markers}
            hoveredMarkerId={hoveredMarkerId}
            setHoveredMarkerId={setHoveredMarkerId}
            googleMapURL={`https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry,drawing,places`}
            loadingElement={<div style={{ height: `100%` }} />}
            containerElement={<div style={{ height: `100%`, width: "100%" }} />}
            mapElement={<div style={{ height: `100%`, width: "100%" }} />}
          />
        </Box>
      )}
    </Box>
  );
};

export default AllTrackers;
